import React, { useEffect, useState } from "react";
import { MobileNavigationProps } from "./types";

const CameraPermissionScreen: React.FC<MobileNavigationProps> = ({
  navigateToStep,
  logStep,
}) => {
  const [isRequesting, setIsRequesting] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if camera permission is already granted on mount
  useEffect(() => {
    checkCameraPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkCameraPermission = async () => {
    setIsChecking(true);
    // First check browser's permission state
    try {
      const permissionStatus = await navigator.permissions.query({
        name: "camera" as PermissionName,
      });

      if (permissionStatus.state === "granted") {
        // Browser-level permission is granted — require the persisted app flag too
        const persisted = localStorage.getItem("cameraPermissionGranted");
        if (persisted === "true") {
          if (logStep) {
            logStep("camera_permission_granted");
          }
          navigateToStep("faceScanning");
          return;
        }
        // Browser granted but app flag missing -> show dialog so user can confirm
        setIsChecking(false);
        return;
      } else if (permissionStatus.state === "prompt") {
        // Need to request permission via modal
        setIsChecking(false);
        return;
      } else if (permissionStatus.state === "denied") {
        // Previously denied
        if (logStep) {
          logStep("camera_permission_denied", { error_message: "Browser camera permission denied" });
        }
        navigateToStep("permissionDenied");
        return;
      }
    } catch (error) {
      // Permissions API not supported - fall back to localStorage check
      console.log("Permission API not supported, will fallback to localStorage check");
    }

    // If Permissions API didn't indicate granted, fall back to localStorage
    const cameraPermission = localStorage.getItem("cameraPermissionGranted");
    if (cameraPermission === "true") {
      if (logStep) {
        logStep("camera_permission_granted");
      }
      navigateToStep("faceScanning");
      return;
    }

    // Permission not granted, show the dialog
    setIsChecking(false);
  };

  const handleOkClick = async () => {
    setIsRequesting(true);
    try {
      // Request camera permission from browser
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" }
      });

      // Permission granted! Stop the stream and save to localStorage
      stream.getTracks().forEach(track => track.stop());
      localStorage.setItem("cameraPermissionGranted", "true");

      // Log camera permission granted (non-blocking)
      if (logStep) {
        logStep("camera_permission_granted");
      }

      // Navigate to face scanning screen
      navigateToStep("faceScanning");
    } catch (error) {
      console.error("Camera permission denied:", error);

      // Log camera permission denied (non-blocking)
      if (logStep) {
        logStep("camera_permission_denied", {
          error_message: "User denied camera permission",
        });
      }

      // Permission denied - navigate to permission denied screen
      navigateToStep("permissionDenied");
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDontAllow = () => {
    // Log camera permission denied (non-blocking)
    if (logStep) {
      logStep("camera_permission_denied", {
        error_message: "User chose not to allow camera permission",
      });
    }

    // Navigate to permission denied page
    navigateToStep("permissionDenied");
  };

  // Show nothing while checking permission (seamless transition)
  if (isChecking) {
    return null;
  }

  return (
    <div
      className="d-md-none w-100"
      style={{
        background: "linear-gradient(180deg, #5e0000 0%, #5e0000 100%)",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: "Sweet Sans Pro Regular",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "20px",
          textAlign: "left",
          maxWidth: "300px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
        }}
      >
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "400",
            marginBottom: "8px",
            color: "#000",
            fontFamily: "Sweet Sans Pro Regular",
          }}
        >
          &ldquo;Senco Gold&rdquo; Would Like To Access Your Camera
        </h3>
        <p
          style={{
            fontSize: "13px",
            color: "#666",
            marginBottom: "20px",
            lineHeight: "1.4",
          }}
        >
          This app requires access to camera
        </p>
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleDontAllow}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "9px 10px",
              backgroundColor: "#f0f0f0",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#333",
              cursor: isRequesting ? "not-allowed" : "pointer",
              opacity: isRequesting ? 0.6 : 1,
              fontFamily: "Sweet Sans Pro Regular",
            }}
          >
            Don&apos;t Allow
          </button>
          <button
            onClick={handleOkClick}
            disabled={isRequesting}
            style={{
              flex: 1,
              padding: "9px 10px",
              backgroundColor: "#007AFF",
              border: "none",
              borderRadius: "20px",
              fontSize: "15px",
              fontWeight: "500",
              color: "white",
              cursor: isRequesting ? "not-allowed" : "pointer",
              opacity: isRequesting ? 0.6 : 1,
              fontFamily: "Sweet Sans Pro Regular",
            }}
          >
            {isRequesting ? "Requesting..." : "OK"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraPermissionScreen;
